/**
 * The render's stylesheets.
 *
 * DIV-024 moved the stylesheet requests from after the config call to the
 * moment a calculator was known to exist for the page, so they overlapped the
 * config call. DIV-027 (one resolve request) then made the id and the config
 * arrive TOGETHER, which silently took that overlap away again: production
 * 2026-09-25 showed `lookups+config` at 2 ms but `config→mount` up from 205 to
 * 352 ms at the mobile median, the store CSS now being the serial request.
 *
 * DIV-034: `plugins.css` is requested at boot, from DetectPage, next to the
 * resolve and product-.js prefetches, on every page that has a product id.
 *
 * DIV-035 (#116 item 33): `store/style/app` is no longer requested at all when
 * the API is current. What it served was the calculator's base stylesheet plus
 * the merchant's own CSS; the base stylesheet now ships as the first section of
 * `plugins.css` (src/styles/store-base.css) and the merchant's CSS arrives in the
 * calculator payload as `customCss`, injected here as an inline `<style>`. The
 * request is still made when the payload has NO `customCss` key — an older API,
 * or the builder preview's direct `window.cpc.renderCalculator` entry, which has
 * no payload — so nothing depends on deploy order.
 *
 * Element ids are unchanged (`cdn-plugin-css`, `cpc-style`): `loadStyle` dedupes
 * by id, and the inline `<style>` takes the link's id so the same dedupe holds.
 */
import { appState } from "../state/app-state";
import { loadStyle } from "./load-style";

let pluginsInFlight: Promise<unknown> | undefined;
let storeCssInFlight: Promise<unknown> | undefined;

/** plugins.css (base stylesheet + plugin styles) — requested once, at boot. */
export function requestStyles(): Promise<unknown> {
  pluginsInFlight ??= document.getElementById("cdn-plugin-css") ? Promise.resolve() : loadStyle(`${appState.cdnURL}/plugins.css`, "cdn-plugin-css");
  return pluginsInFlight;
}

/**
 * The store's own CSS. `customCss` is the payload's value: a string to inject,
 * null for "the store has none", undefined for "no such key" (older API / no
 * payload) — only that last case still requests `store/style/app`. First call
 * wins; later calls (the render's wait, the preview entry) reuse it.
 */
export function applyStoreCss(customCss: string | null | undefined): Promise<unknown> {
  storeCssInFlight ??= customCss === undefined
    ? loadStyle(`${appState.baseCalculatorNewApiUrl}store/style/app?shop=${appState.cpc_shopUrl}`, "cpc-style")
    : injectStoreCss(customCss);
  return storeCssInFlight;
}

/** Everything the render waits for before building the calculator's HTML. */
export function stylesReady(): Promise<unknown> {
  return Promise.all([requestStyles(), storeCssInFlight ?? applyStoreCss(undefined)]);
}

function injectStoreCss(css: string | null): Promise<void> {
  if (css !== null && css.trim() !== "" && !document.getElementById("cpc-style")) {
    const style = document.createElement("style");
    style.id = "cpc-style";
    style.textContent = css;
    document.head.appendChild(style);
  }
  return Promise.resolve();
}
